/**
 * FIX BRAND NAME - One-time utility
 * 
 * Changes "Emily de Molly Nail Colour" → "Emily de Molly"
 * 
 * Add to routes, run once, then remove.
 * Route suggestion: /fix-brand
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import { ArrowLeft, Wrench, Check, Loader2, AlertCircle } from 'lucide-react';

const OLD_BRAND = "Emily de Molly Nail Colour";
const NEW_BRAND = "Emily de Molly";

export default function FixBrandName() {
  const navigate = useNavigate();
  
  const [stage, setStage] = useState<'ready' | 'running' | 'complete'>('ready');
  const [found, setFound] = useState<number>(0);
  const [updated, setUpdated] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const handleFix = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      setError('You must be logged in');
      return;
    }

    setStage('running');
    setError(null);

    try {
      // 1. Find all items with the old brand name
      const itemsRef = collection(db, 'users', currentUser.uid, 'items');
      const snapshot = await getDocs(itemsRef);
      
      const itemsToFix: string[] = [];
      
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.brand === OLD_BRAND) {
          itemsToFix.push(docSnap.id);
        }
      });

      setFound(itemsToFix.length);

      if (itemsToFix.length === 0) {
        setStage('complete');
        return;
      }

      // 2. Update in batches
      const BATCH_SIZE = 500;
      let batch = writeBatch(db);
      let batchCount = 0;
      let totalUpdated = 0;

      for (const itemId of itemsToFix) {
        const itemRef = doc(db, 'users', currentUser.uid, 'items', itemId);
        batch.update(itemRef, { brand: NEW_BRAND });
        batchCount++;
        totalUpdated++;

        if (batchCount >= BATCH_SIZE) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      // Commit remaining
      if (batchCount > 0) {
        await batch.commit();
      }

      setUpdated(totalUpdated);
      setStage('complete');

    } catch (err) {
      console.error('Error fixing brand:', err);
      setError(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setStage('ready');
    }
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-on-bg transition-default font-body"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        {/* Header */}
        <div className="glass-card p-6 mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500 mb-4">
            <Wrench className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-glass mb-2">Fix Brand Name</h1>
          <p className="text-glass-secondary text-sm">
            One-time fix for misspelled brand
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="glass-card p-4 mb-6 border-l-4 border-red-500 bg-red-500/10">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Ready Stage */}
        {stage === 'ready' && (
          <div className="glass-card p-6">
            <div className="mb-6">
              <div className="text-glass-muted text-sm mb-1">Will change:</div>
              <div className="bg-red-500/20 text-red-300 px-3 py-2 rounded-lg font-mono text-sm mb-2">
                "{OLD_BRAND}"
              </div>
              <div className="text-glass-muted text-sm mb-1">To:</div>
              <div className="bg-green-500/20 text-green-300 px-3 py-2 rounded-lg font-mono text-sm">
                "{NEW_BRAND}"
              </div>
            </div>

            <button
              onClick={handleFix}
              className="w-full px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-default flex items-center justify-center gap-2"
            >
              <Wrench className="w-5 h-5" />
              Find & Fix Items
            </button>
          </div>
        )}

        {/* Running Stage */}
        {stage === 'running' && (
          <div className="glass-card p-8 text-center">
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-glass mb-2">Fixing...</h2>
            <p className="text-glass-secondary">Finding and updating items</p>
          </div>
        )}

        {/* Complete Stage */}
        {stage === 'complete' && (
          <div className="glass-card p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500 mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-glass mb-2">Done!</h2>
            
            {found === 0 ? (
              <p className="text-glass-secondary">
                No items found with "{OLD_BRAND}"
              </p>
            ) : (
              <p className="text-glass-secondary">
                Updated <span className="text-green-400 font-bold">{updated}</span> items to "{NEW_BRAND}"
              </p>
            )}

            <button
              onClick={() => navigate('/collection')}
              className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-default"
            >
              Back to Collection
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
